// create-friend-white analytics helpers.
//
// All analytics flows through GTM (window.dataLayer.push) — this lander never
// talks to Mixpanel directly (no SDK / token). The GTM container (per-domain:
// GTM-N8LQSVFS for swipeyconnect, GTM-PNXR5M33 for swipeyquiz — see
// src/config/domainConfig.js) holds the Mixpanel token and is configured to
// forward ONLY `registration_started` to Mixpanel; every other event below
// stays GTM/GA-only. `lp_source` lets the container tell this lander's traffic
// apart from the other landers.
//
// Event catalogue:
//   style_selected               — first screen, style picked          (GTM/GA only)
//   step_2 … step_6              — quiz steps + summary screen          (GTM/GA only)
//   lander_registration_modal    — registration form (AuthModal) opens  (GTM/GA only)
//   paywall_popup_plan_selected  — plan picked on the pre-signup popup   (GTM/GA only)
//                                  (paywall_popup + order=popup_first only)
//   registration_started         — reg attempt (email submit / Google)  (→ Mixpanel)
//   lander_registration_completed— reg succeeded (carries user email)    (GTM/GA only)
//   lander_login_completed       — email/password login succeeded       (GTM/GA only)
//   lander_forgot_pass_completed — password reset completed             (GTM/GA only)

import { growthbook, gbReady } from '../lib/growthbookClient';
import {
  PAYWALL_NAV_CONFIG_FLAG,
  DEFAULT_NAV_CONFIG,
  resolveNavConfig,
  resolveAuthType,
} from './authType';
import { getLpSource } from '../config/domainConfig'

// Lander identifier — sent on every GTM event so the shared container can
// filter cohorts by which LP the user came from. Mirrors api.js LP_SOURCE.
// Resolved from the live hostname (swipeyconnect vs swipeyquiz) — see
// src/config/domainConfig.js.
const LP_SOURCE = getLpSource()

// `lander-content` — identifies which GrowthBook content-config variant is
// live, taken from the config's `name` field. Sent on EVERY event alongside
// `lp_source`. Defaults to 'default' until GrowthBook resolves and the provider
// calls setLanderContent().
// `createsexfriends-white-paywall-nav-config` — the resolved post-signup arm, sent on
// EVERY event under the GrowthBook flag's own key so the shared container reads the
// same key it already sees in `ab_testing`. Same value Everflow reports as sub7
// (utils/everflow) — one resolver, utils/authType, so GTM and Everflow can never
// disagree about which arm the user was in.
//
// Read live off the shared client instead of being pushed in from React: that keeps
// it correct for the sticky arms (resolveNavConfig) with no provider wiring. Stays
// empty until GB settles — an empty value means "not resolved yet", never a guess.
let gbSettled = false;
gbReady.then(() => { gbSettled = true }).catch(() => { gbSettled = true });

export const getPaywallNavArm = () => {
  if (!gbSettled) return '';
  try {
    return resolveAuthType(
      resolveNavConfig(
        growthbook.getFeatureValue(PAYWALL_NAV_CONFIG_FLAG, DEFAULT_NAV_CONFIG),
      ),
    );
  } catch (e) {
    return '';
  }
};

let landerContent = 'default'

// Called by GrowthBookProvider once the GB content config resolves, passing the
// config's `name`. No-ops on empty/missing names so events keep the default.
export const setLanderContent = (name) => {
  if (name) landerContent = name
}

// Read the current resolved lander-content — used by utils/serverGtm.js and
// utils/mixpanel.js so their events carry the same attribution as the dataLayer
// events.
export const getLanderContent = () => landerContent

export const trackEvent = (eventName, eventParams = {}) => {
  try {
    window.dataLayer = window.dataLayer || []

    // Every event carries `lp_source` + `lander-content`. Spread caller params
    // last so an explicit override still wins.
    const eventData = {
      event: eventName,
      lp_source: LP_SOURCE,
      'lander-content': landerContent,
      [PAYWALL_NAV_CONFIG_FLAG]: getPaywallNavArm(),
      ...eventParams,
      timestamp: new Date().toISOString(),
    }

    window.dataLayer.push(eventData)

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', eventName, eventParams)
    }

    return true
  } catch (error) {
    console.error('Analytics tracking error:', error)
    return false
  }
}

// ---- Quiz step tracking ----------------------------------------------------

// First screen — fires when the user picks a style. Renamed from the old
// `step_1`; the remaining quiz answers keep the `step_2` … `step_6` names.
export const trackStyleSelected = (style) => {
  return trackEvent('style_selected', {
    step_name: 'Style',
    style: style,
  })
}

const trackStepComplete = (stepNumber, stepData = {}) => {
  return trackEvent(`step_${stepNumber}`, {
    step_number: stepNumber,
    ...stepData,
  })
}

export const trackStep2 = (age, ethnicity) => {
  return trackStepComplete(2, {
    step_name: 'Ethnicity',
    age: age,
    ethnicity: ethnicity,
  })
}

export const trackStep3 = (bodyType, breastSize, buttSize) => {
  return trackStepComplete(3, {
    step_name: 'Body',
    body: bodyType,
    breast: breastSize,
    booty: buttSize,
  })
}

export const trackStep4 = (hairStyle, hairColor, eyeColor) => {
  return trackStepComplete(4, {
    step_name: 'Hair',
    hair_style: hairStyle,
    hair_color: hairColor,
    eye_color: eyeColor,
  })
}

export const trackStep5 = (voice, relationship) => {
  return trackStepComplete(5, {
    step_name: 'Voice',
    voice: voice,
    relationship: relationship,
  })
}

// Summary screen reached (Step5 / QuizFinal). The 6th and final quiz screen.
export const trackStep6 = () => {
  return trackStepComplete(6, { step_name: 'Summary' })
}

// ---- Registration / auth ---------------------------------------------------

// Fires when the registration form (AuthModal) opens, from any entry point.
export const trackRegistrationModalOpened = () => {
  return trackEvent('lander_registration_modal')
}

// Registration attempt started (email submit or Google sign-up).
//
// NOTE: the dataLayer/GTM event name is HYPHENATED (`registration-started`) to
// match the platform + sGTM convention (serverGtm.js trackServerRegistrationStarted).
// The Mixpanel copy is a SEPARATE event named `registration_started` (underscore),
// sent directly + identified from utils/mixpanel.js for the paywall_popup flow; the
// GTM container should NOT also forward this to Mixpanel or it double-counts on an
// anonymous id.
//
// Dual signature: legacy callers pass a `source` STRING (`'google'`/`'manual'`);
// the paywall_popup flow passes a full params OBJECT (paywall-type/flow/source/
// email/click-ids/utm) mirroring the sibling create-friend lander so the popup
// cohort's dataLayer event carries the same attribution as its sGTM copy.
export const trackRegistrationStarted = (arg = {}) => {
  const params = typeof arg === 'string' ? (arg ? { source: arg } : {}) : (arg || {})
  return trackEvent('registration-started', params)
}

// Registration succeeded. Fires `lander_registration_completed` (GTM/GA-only),
// carrying the user's email when we have it (manual signup). `email` is omitted
// from the dataLayer payload when unavailable (e.g. Google OAuth).
export const trackRegistrationCompleted = (source, email) => {
  const params = {
    ...(source ? { source } : {}),
    ...(email ? { email } : {}),
  }
  return trackEvent('lander_registration_completed', params)
}

// Fires on successful email/password login. GTM/GA-only.
export const trackLoginCompleted = () => {
  return trackEvent('lander_login_completed')
}

// Fires after a password reset successfully completes (new password set).
// GTM/GA-only.
export const trackForgotPasswordCompleted = () => {
  return trackEvent('lander_forgot_pass_completed')
}

// === Paywall-popup checkout funnel (GTM) ===
// The regular (platform) paywall emits order_created / subscription_purchase to
// its GTM container. In the in-place paywall_popup the user may never reach the
// platform, so the popup emits the equivalent LP dataLayer events here so the
// funnel matches the regular paywall.

// Fires when the user opens checkout from the popup (taps Continue).
//
// In the `popup_first` order the popup is shown BEFORE registration, so the tap on
// Continue can't emit this yet: there is no user to attribute it to (Mixpanel needs
// the Mongo _id for the dedupe key) and it would land ahead of registration_started
// in the funnel. That tap emits `paywall_popup_plan_selected` below instead, and
// `order_created` fires from createAccount right before the (now authorized)
// checkout call — so in BOTH arms order_created still means "checkout opened" and
// still follows registration_started.
export const trackOrderCreated = (details = {}) => {
  return trackEvent('order_created', details)
}

// popup_first only: the unauthenticated visitor picked a plan and tapped Continue
// on the pre-signup popup. Marks the popup→signup hand-off, so the popup-first arm
// has a measurable step between `lander_registration_modal` and
// `registration-started` (drop-off between choosing a plan and registering).
// GTM/GA-only — there's no user identity yet.
export const trackPaywallPlanSelected = (details = {}) => {
  return trackEvent('paywall_popup_plan_selected', details)
}

// Subscription payment succeeded. NOTE: in paywall_popup the payment completes on
// the Upgate hosted page and the user is redirected to the platform (/lobby), so
// this normally CANNOT fire from the LP popup — it is owned by the platform.
// Exported for parity / any non-redirecting caller.
export const trackSubscriptionPurchase = (details = {}) => {
  return trackEvent('subscription_purchase', details)
}

export default {
  trackEvent,
  trackStyleSelected,
  trackStep2,
  trackStep3,
  trackStep4,
  trackStep5,
  trackStep6,
  trackRegistrationModalOpened,
  trackRegistrationStarted,
  trackRegistrationCompleted,
  trackLoginCompleted,
  trackForgotPasswordCompleted,
  trackOrderCreated,
  trackPaywallPlanSelected,
  trackSubscriptionPurchase,
}
